import { CardActions } from '@material-ui/core';
import {
  Avatar, Button, CardContent, CardHeader, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Drawer, Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import log from 'loglevel';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import {
 AtsIntegration, DateValidationFields, LoginAsAdminResponse, ValidationFields,
} from '../../../@types/Account';
import AccountDeleteDialog from '../../../components/Dialogs/AccountDeleteDialog';
import { ErrorsStack } from '../../../components/ErrorsStack';
import { VIEW_MODE } from '../../../consts/enums';
import { PERMISSIONS } from '../../../consts/permissions';
import { useDisconnectAtsIntegration, useGetAccount, useUploadAccountImage } from '../../../hooks/useAccount';
import { useAuth } from '../../../hooks/useAuth';
import { useLoginAsAdmin } from '../../../hooks/useLoginAsAdmin';
import useNavigate from '../../../hooks/useNavigate';
import STRING_KEYS from '../../../language/keys';
import { AccountAssociatedCompany } from '../AccountAssociatedCompany';
import { AccountEditPanelHeader } from '../AccountEditPanelHeader';
import { DeleteAccountButton } from '../DeleteAccountButton';
import { PRESERVED_ACCOUNTS_FILTERS_PARAM_KEY } from '../index';
import { style } from './style';

export type AccountAssociatedFields = {
  name: string;
  subDomainName: ValidationFields;
  accountStatus: ValidationFields;
  logo: string;
  displayName: ValidationFields;
  maximumOpenPositions: ValidationFields;
  maxUserSeats: ValidationFields;
  userSeatsPlanLimit: number;
  creditsUsed: number;
  totalCredits: number;
  isCreditsBased: ValidationFields;
  addCredits: ValidationFields;
  recruitingUsageType: ValidationFields;
  outreachEnabledForTrial: boolean;
  persistedOutreachEnabledForTrial: boolean;
};

type AccountEditPanelContainerProps = {
  isOpen: boolean;
  onCloseClick: () => void;
  onValuesChange: (name: string, value: string | boolean) => void;
  onSubmitForm: () => void;
  isLoadingAccount: boolean;
  accountId: string;
  accountAssociatedFields: AccountAssociatedFields;
  onDeleteAccount: (accountId: string) => void;
  linkedinUrl: string;
  isError: boolean;
  errorMessage: string;
  isUpdatingAccount: boolean;
  isErrorAccount: boolean;
  accountErrorMessage: string;
  viewMode: ViewMode;
  startDatePaidPlan: DateValidationFields;
  endDatePaidPlan: DateValidationFields;
  pricePlan: string;
  billing: string;
  onChangeDateField: (name: string, date: Date | string) => void;
  onChangePaidPlanStartDate: (name: string, date: Date | string) => void;
  isErrorUpdateAccountPlan: boolean;
  errorMessageUpdateAccountPlan: string;
  isErrorCreateAccountPlan: boolean;
  errorMessageCreateAccountPlan: string;
  fieldsErrorsUpdateAccountPlan: string[];
  fieldsErrorsCreateAccountPlan: string[];
  isUpdateAccountPlanLoading: boolean;
  isCreateAccountPlanLoading: boolean;
  isDeleteAccountPlanLoading: boolean;
  isDeleteAccountPlanError: boolean;
  errorMessageDeleteAccountPlan: string;
  onOpenAssociatedAccountDialogClick: () => void;
  errorsValidation: string[];
};
const TAG = 'AccountEditPanelContainer';

export const AccountEditPanelContainer = ({
  isOpen,
  onCloseClick,
  onValuesChange,
  onSubmitForm,
  accountAssociatedFields,
  accountId,
  onDeleteAccount,
  linkedinUrl,
  isError,
  errorMessage,
  isUpdatingAccount,
  isLoadingAccount,
  isErrorAccount,
  accountErrorMessage,
  viewMode = VIEW_MODE.READ_ONLY,
  pricePlan,
  startDatePaidPlan,
  endDatePaidPlan,
  billing,
  onChangeDateField,
  onChangePaidPlanStartDate,
  isErrorUpdateAccountPlan,
  errorMessageUpdateAccountPlan,
  isErrorCreateAccountPlan,
  errorMessageCreateAccountPlan,
  fieldsErrorsUpdateAccountPlan,
  fieldsErrorsCreateAccountPlan,
  isCreateAccountPlanLoading,
  isUpdateAccountPlanLoading,
  errorMessageDeleteAccountPlan,
  isDeleteAccountPlanLoading,
  isDeleteAccountPlanError,
  onOpenAssociatedAccountDialogClick,
  errorsValidation,
}: AccountEditPanelContainerProps): JSX.Element => {
  const { goToAccountEdit } = useNavigate();
  const { t: translate } = useTranslation();
  const { hasPermissions } = useAuth();
  const [searchParams] = useSearchParams();
  const [isDeleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [isErrorUploadImage, setErrorUploadImage] = useState<{
    error: boolean;
    errorMessage: string;
  }>({
    error: false,
    errorMessage: '',
  });

  const [isAtsDisconnectDialogOpen, setIsAtsDisconnectDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const { account: accountData } = useGetAccount(accountId);
  const activeAtsIntegration = accountData?.atsIntegrations?.find((ats: AtsIntegration) => ats.isActive);

  const { mutate: disconnectAts, isLoading: isDisconnectingAts } = useDisconnectAtsIntegration(() => {
    queryClient.invalidateQueries(['account-data', accountId]);
    setIsAtsDisconnectDialogOpen(false);
  });

  const b64Value = searchParams.get(PRESERVED_ACCOUNTS_FILTERS_PARAM_KEY);
  const uploadImageAccount = useUploadAccountImage(() => {
    setErrorUploadImage({
      error: true,
      errorMessage: translate(
        STRING_KEYS.ACCOUNTS_EDIT_ERROR_MESSAGE_UPLOAD_IMAGE,
      ),
    });
  });

  const loginAsAdmin = useLoginAsAdmin(
    (loginAsAdminResponse: LoginAsAdminResponse) => {
      window.open(loginAsAdminResponse.frontendUrl, '_blank');
    },
  );

  const handleDeleteDialogVisibleClick = (bool: boolean) => {
    setDeleteDialogVisible(bool);
  };

  const handleInputUploadImage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const myFile = e.target.files ? e.target.files[0] : '';
      const formData = new FormData();
      formData.append('file', myFile);

      uploadImageAccount.mutateAsync(formData, {
        onSuccess: (resp: { fileUrl: string }) => {
          onValuesChange('logo', resp.fileUrl);
          log.debug(TAG, resp);
        },
      });
    },
    [uploadImageAccount, onValuesChange],
  );

  const disable =
    isUpdatingAccount ||
    isErrorAccount ||
    isLoadingAccount ||
    loginAsAdmin.isLoading ||
    isCreateAccountPlanLoading ||
    isUpdateAccountPlanLoading ||
    isDeleteAccountPlanLoading;

  const handleLoginAsAdmin = () => {
    loginAsAdmin.mutate({
      accountId,
    });
  };
  const isDisabled = viewMode === VIEW_MODE.READ_ONLY;
  const isEditable = viewMode === VIEW_MODE.EDITABLE;

  const renderBodyContent = () => {
    if (
      isUpdatingAccount ||
      isLoadingAccount ||
      loginAsAdmin.isLoading ||
      isCreateAccountPlanLoading ||
      isUpdateAccountPlanLoading ||
      isDeleteAccountPlanLoading
    ) {
      return (
        <Box sx={ style.card.circular }>
          <CircularProgress />
        </Box>
      );
    }

    if (isErrorAccount) {
      return (
        <Box sx={ style.card.box.sx }>
          <ErrorsStack errors={ [] } errorMessage={ accountErrorMessage } />
        </Box>
      );
    }

    return (
      <AccountAssociatedCompany
        accountAssociatedFields={ accountAssociatedFields }
        onValuesChange={ onValuesChange }
        onInputUploadImage={ handleInputUploadImage }
        isUploadLoading={ uploadImageAccount.isLoading }
        accountId={ accountId }
        isErrorUploadImage={ isErrorUploadImage.error }
        isErrorUploadImageMessage={ isErrorUploadImage.errorMessage }
        linkedinUrl={ linkedinUrl }
        onLoginAsAdminClick={ handleLoginAsAdmin }
        viewMode={ viewMode }
        pricePlan={ pricePlan }
        startDatePaidPlan={ startDatePaidPlan }
        endDatePaidPlan={ endDatePaidPlan }
        billing={ billing }
        onChangeDateField={ onChangeDateField }
        isEditable={ isEditable }
        onChangePaidPlanStartDate={ onChangePaidPlanStartDate }
        onAssociatedCompanyOpenClick={ onOpenAssociatedAccountDialogClick }
        errorsValidation={ errorsValidation }
      />
    );
  };
  const renderButton = () => {
    if (viewMode === VIEW_MODE.EDITABLE) {
      return (
        <Button
          color="primary"
          type="submit"
          fullWidth
          variant="contained"
          disabled={ disable }
        >
          {translate(STRING_KEYS.SAVE_CHANGES)}
        </Button>
      );
    }
    if (hasPermissions([PERMISSIONS.ACCOUNT_EDIT])) {
      return (
        <Button
          color="secondary"
          type="button"
          fullWidth
          variant="contained"
          sx={ style.buttonEdit.sx }
          onClick={ (e) => {
            e.preventDefault();
            goToAccountEdit(accountId, searchParams.toString(), b64Value);
          } }
        >
          {translate(STRING_KEYS.EDIT)}
        </Button>
      );
    }

    return null;
  };

  return (
    <Drawer
      anchor="right"
      open={ isOpen }
      variant="temporary"
      sx={ style.card.drawler }
    >
      <form style={ style.card.sx } onSubmit={ onSubmitForm }>
        <CardHeader
          sx={ style.card.header }
          title={ (
            <AccountEditPanelHeader
              title={ translate(STRING_KEYS.ACCOUNT_PANEL_TITLE) }
              onCloseClick={ onCloseClick }
            />
          ) }
        />
        <CardContent sx={ style.card.CardContent }>
          {isDeleteAccountPlanError && (
            <Box sx={ style.card.box.sx }>
              <ErrorsStack
                errors={ [] }
                errorMessage={ errorMessageDeleteAccountPlan }
              />
            </Box>
          )}
          {isError && (
            <Box sx={ style.card.box.sx }>
              <ErrorsStack errors={ [] } errorMessage={ errorMessage } />
            </Box>
          )}
          {loginAsAdmin.isError && (
            <Box sx={ style.card.box.sx }>
              <ErrorsStack
                errors={ [] }
                errorMessage={
                  loginAsAdmin.error?.response?.data?.message ||
                  translate(STRING_KEYS.ERROR_LOGIN_AS_ADMIN)
                }
              />
            </Box>
          )}
          {isErrorUpdateAccountPlan && (
            <Box sx={ style.card.box.sx }>
              <ErrorsStack
                errors={ fieldsErrorsUpdateAccountPlan }
                errorMessage={ errorMessageUpdateAccountPlan }
              />
            </Box>
          )}
          {isErrorCreateAccountPlan && (
            <Box sx={ style.card.box.sx }>
              <ErrorsStack
                errors={ fieldsErrorsCreateAccountPlan }
                errorMessage={ errorMessageCreateAccountPlan }
              />
            </Box>
          )}
          {renderBodyContent()}
          {activeAtsIntegration && (
            <Box sx={ { p: 2, borderTop: '1px solid', borderColor: 'divider' } }>
              <Typography variant="subtitle2" sx={ { mb: 1 } }>
                {translate(STRING_KEYS.ATS_INTEGRATION)}
              </Typography>
              <Box sx={ { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } }>
                <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                  {activeAtsIntegration.atsImage && (
                    <Avatar src={ activeAtsIntegration.atsImage } sx={ { width: 24, height: 24 } } />
                  )}
                  <Box>
                    <Typography variant="body2">{activeAtsIntegration.atsName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {translate(STRING_KEYS.ATS_LINKED_AT)}: {new Date(activeAtsIntegration.linkedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={ () => setIsAtsDisconnectDialogOpen(true) }
                >
                  {translate(STRING_KEYS.DISCONNECT_ATS)}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
        <CardActions style={ style.CardActions.sx }>
          {!isUpdatingAccount && !isDisabled && (
            <Box style={ style.CardActions.boxDelete.sx }>
              <DeleteAccountButton
                isDisable={ disable }
                onDeleteClick={ () => {
                  handleDeleteDialogVisibleClick(true);
                } }
              />
            </Box>
          )}
          {renderButton()}
        </CardActions>
        <AccountDeleteDialog
          accountName={ accountAssociatedFields?.displayName?.value || '' }
          isOpen={ isDeleteDialogVisible }
          onConfirm={ () => {
            onDeleteAccount(accountId);
            handleDeleteDialogVisibleClick(false);
            onCloseClick();
          } }
          onClose={ () => setDeleteDialogVisible(false) }
        />
        <Dialog
          open={ isAtsDisconnectDialogOpen }
          onClose={ () => setIsAtsDisconnectDialogOpen(false) }
        >
          <DialogTitle>{translate(STRING_KEYS.DISCONNECT_ATS_CONFIRMATION_TITLE)}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {translate(STRING_KEYS.DISCONNECT_ATS_CONFIRMATION_MESSAGE).replace('{atsName}', activeAtsIntegration?.atsName || '')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={ () => setIsAtsDisconnectDialogOpen(false) }>
              {translate(STRING_KEYS.CANCEL)}
            </Button>
            <Button
              onClick={ () => disconnectAts(accountId) }
              color="error"
              variant="contained"
              disabled={ isDisconnectingAts }
            >
              {translate(STRING_KEYS.DISCONNECT_ATS)}
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </Drawer>
  );
};
